import jsPDF from "jspdf";

// ─── Constants ───
const MARGIN = 18;
const PAGE_WIDTH = 210; // A4
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const AMBER = [245, 158, 11];
const DARK_BG = [20, 20, 24];
const HEADING_COLOR = [30, 30, 35];
const LABEL_COLOR = [100, 100, 110];
const MUTED_COLOR = [160, 160, 170];

// ─── Helpers ───
function normalizeGearAssignments(assignments) {
  if (!assignments) return [];
  return assignments.map(a =>
    typeof a === "string" ? { gear_id: a, quantity: 1 } : { gear_id: a.gear_id, quantity: a.quantity || 1 }
  );
}

function buildLookupMaps(gearList, crewList) {
  const gearMap = {};
  gearList.forEach(g => { gearMap[g.gear_id] = g; });
  const crewMap = {};
  crewList.forEach(c => { crewMap[c.crew_id] = c; });
  return { gearMap, crewMap };
}

// ─── PDF Section Builders ───
function drawHeader(doc) {
  let y = MARGIN;
  doc.setFillColor(...DARK_BG);
  doc.rect(0, 0, PAGE_WIDTH, 38, "F");
  doc.setFillColor(...AMBER);
  doc.rect(0, 37, PAGE_WIDTH, 1.2, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...AMBER);
  doc.text("PRODUCTION HUB", MARGIN, 16);

  doc.setFontSize(10);
  doc.setTextColor(...MUTED_COLOR);
  doc.text("EVENT SHEET", MARGIN, 24);

  doc.setFontSize(9);
  const dateStr = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  doc.text(`Generated: ${dateStr}`, PAGE_WIDTH - MARGIN, 24, { align: "right" });

  return 48;
}

function drawSectionHeader(doc, y, title) {
  doc.setFillColor(245, 245, 248);
  doc.roundedRect(MARGIN, y - 4, CONTENT_WIDTH, 8, 1, 1, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...LABEL_COLOR);
  doc.text(title.toUpperCase(), MARGIN + 4, y + 1);
  return y + 10;
}

function drawDetail(doc, y, label, value) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...LABEL_COLOR);
  doc.text(label, MARGIN + 4, y);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...HEADING_COLOR);
  doc.text(String(value || "\u2014"), MARGIN + 40, y);
  return y + 6;
}

function drawEventDetails(doc, y, event) {
  const statusColors = { CONFIRMED: [16, 185, 129], PENDING: [245, 158, 11], COMPLETED: [99, 102, 241], CANCELLED: [239, 68, 68] };

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...HEADING_COLOR);
  doc.text(event.name || "Untitled Event", MARGIN, y);
  y += 8;

  // Status badge
  const status = (event.status || "pending").toUpperCase();
  doc.setFontSize(8);
  const sc = statusColors[status] || MUTED_COLOR;
  doc.setTextColor(sc[0], sc[1], sc[2]);
  doc.text(status, MARGIN, y);
  y += 10;

  y = drawSectionHeader(doc, y, "Event Details");
  y = drawDetail(doc, y, "Client", event.client_name);
  y = drawDetail(doc, y, "Venue", event.venue || "TBD");
  y = drawDetail(doc, y, "Start", event.start_date);
  y = drawDetail(doc, y, "End", event.end_date);

  if (event.notes) {
    y = drawDetail(doc, y, "Notes", "");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(60, 60, 70);
    const noteLines = doc.splitTextToSize(event.notes, CONTENT_WIDTH - 8);
    doc.text(noteLines, MARGIN + 4, y);
    y += noteLines.length * 4.5;
  }
  return y + 6;
}

function drawGearSection(doc, y, gearAssignments, gearMap) {
  y = drawSectionHeader(doc, y, `Assigned Gear (${gearAssignments.length} items)`);

  if (gearAssignments.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED_COLOR);
    doc.text("No gear assigned", MARGIN + 4, y);
    return y + 6;
  }

  // Table header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...LABEL_COLOR);
  doc.text("ITEM", MARGIN + 4, y);
  doc.text("CATEGORY", MARGIN + 90, y);
  doc.text("QTY", MARGIN + 130, y);
  y += 2;
  doc.setDrawColor(220, 220, 225);
  doc.line(MARGIN + 4, y, MARGIN + CONTENT_WIDTH - 4, y);
  y += 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  for (const a of gearAssignments) {
    if (y > 270) { doc.addPage(); y = MARGIN; }
    const g = gearMap[a.gear_id];
    doc.setTextColor(...HEADING_COLOR);
    doc.text(g?.name || a.gear_id, MARGIN + 4, y);
    doc.setTextColor(...LABEL_COLOR);
    doc.text(g?.category || "\u2014", MARGIN + 90, y);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...AMBER);
    doc.text(`x${a.quantity}`, MARGIN + 132, y);
    doc.setFont("helvetica", "normal");
    y += 5.5;
  }
  return y + 6;
}

function drawCrewSection(doc, y, crewAssignments, crewMap) {
  if (y > 250) { doc.addPage(); y = MARGIN; }
  y = drawSectionHeader(doc, y, `Assigned Crew (${crewAssignments.length} members)`);

  if (crewAssignments.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED_COLOR);
    doc.text("No crew assigned", MARGIN + 4, y);
    return y + 6;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...LABEL_COLOR);
  doc.text("NAME", MARGIN + 4, y);
  doc.text("ROLE", MARGIN + 70, y);
  doc.text("PHONE", MARGIN + 120, y);
  y += 2;
  doc.setDrawColor(220, 220, 225);
  doc.line(MARGIN + 4, y, MARGIN + CONTENT_WIDTH - 4, y);
  y += 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  for (const id of crewAssignments) {
    if (y > 270) { doc.addPage(); y = MARGIN; }
    const c = crewMap[id];
    doc.setTextColor(...HEADING_COLOR);
    doc.text(c?.name || id, MARGIN + 4, y);
    doc.setTextColor(...LABEL_COLOR);
    doc.text(c?.role || "\u2014", MARGIN + 70, y);
    doc.text(c?.phone || "\u2014", MARGIN + 120, y);
    y += 5.5;
  }
  return y;
}

function drawFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(220, 220, 225);
    doc.line(MARGIN, 282, PAGE_WIDTH - MARGIN, 282);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...MUTED_COLOR);
    doc.text("Production Hub \u2014 Event Sheet", MARGIN, 288);
    doc.text(`Page ${i} of ${pageCount}`, PAGE_WIDTH - MARGIN, 288, { align: "right" });
  }
}

// ─── Main Export Function ───
export function exportEventToPDF(event, gearList, crewList) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const { gearMap, crewMap } = buildLookupMaps(gearList, crewList);
  const gearAssignments = normalizeGearAssignments(event.gear_assignments);

  let y = drawHeader(doc);
  y = drawEventDetails(doc, y, event);
  y = drawGearSection(doc, y, gearAssignments, gearMap);
  y = drawCrewSection(doc, y, event.crew_assignments || [], crewMap);
  drawFooter(doc);

  const safeName = (event.name || "event").replace(/[^a-zA-Z0-9]/g, "_").substring(0, 40);
  doc.save(`${safeName}_event_sheet.pdf`);
}
